import { onClickOutside } from '@vueuse/core';
import type {
	App,
	Directive,
	DirectiveBinding,
} from 'vue';

const STOP_PROPERTY = '__v-click-outside-stop';

type ClickOutsideHandler = (event: PointerEvent) => void;
type HTMLElementWithVClickOutside = HTMLElement & {
	[STOP_PROPERTY]?: () => void;
};
type VClickOutsideBeforeMountDirective = (
	el: HTMLElementWithVClickOutside,
	binding: Pick<DirectiveBinding<ClickOutsideHandler>, 'value'>,
) => void;
type VClickOutsideUnmountedDirective = (el: HTMLElementWithVClickOutside) => void;
type VClickOutsideUpdatedDirective = (
	el: HTMLElementWithVClickOutside,
	binding: Pick<DirectiveBinding<ClickOutsideHandler>, 'oldValue' | 'value'>,
) => void;

function validate(bindingValue: unknown): bindingValue is ClickOutsideHandler {
	if (typeof bindingValue !== 'function') {
		console.warn('v-click-outside: Binding value must be a function');
		return false;
	}

	return true;
}

const unmounted: VClickOutsideUnmountedDirective = (el) => {
	el[STOP_PROPERTY]?.();
	delete el[STOP_PROPERTY];
};

const beforeMount: VClickOutsideBeforeMountDirective = (
	el,
	{ value },
) => {
	unmounted(el);

	if (!validate(value)) {
		return;
	}

	el[STOP_PROPERTY] = onClickOutside(
		el,
		value,
	);
};

const updated: VClickOutsideUpdatedDirective = (
	el,
	{
		value,
		oldValue,
	},
) => {
	if (value === oldValue) {
		return;
	}

	beforeMount(
		el,
		{
			value,
		},
	);
};

const directive: Directive = {
	beforeMount,
	updated,
	unmounted,
};

const plugin = {
	install(app: App): void {
		app.directive(
			'click-outside',
			directive,
		);
	},
	directive,
};

export default plugin;
